/**
 * AI CONTEXT
 * Purpose: renders one attendance category block (students/assistants/makeups/tryouts).
 * Inputs: normalized student rows + status change callback.
 * Uses: AttendanceSectionHeader + AttendanceRow.
 * Used by: TeacherAttendance.
 * Refactor: unify with Admin AttendanceViewStudentListSection to remove duplication.
 */
import { motion } from "motion/react"
import { forwardRef, useCallback, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import Flex from "#blocks/Flex"
import { ensurePortfolioExists } from "#components/PortfolioManagement/ensurePortfolioExists"
import { getPortfolioPagePath } from "#components/PortfolioPage/portfolioPageRoute"
import { CLASS_GROUPS } from "#constants/classOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { AttendanceRowData } from "#customTypes/composedTypes/classTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus } from "#customTypes/types"
import AttendanceRow from "#views/AdminView/AttendanceView/AttendanceRow"
import AttendanceSectionHeader from "#views/AdminView/AttendanceView/AttendanceSectionHeader"

import MissedMakeupSessionSubtitle from "./MissedMakeupSessionSubtitle"
import StudentProjectDaySubtitle from "./StudentProjectDaySubtitle"

const ATTENDANCE_ROW_SIZE = "large"

const TeacherAttendanceSection = forwardRef<
  HTMLDivElement,
  {
    type: "assistants" | "tryouts" | "students" | "makeups" | "exceptions"
    students: AttendanceRowData[]
    sessionId: Uuid | undefined
    session: ClassSessionConstructor | undefined
    classGroup: CLASS_GROUPS
    onStudentAttendanceChange: (
      student: StudentConstructor,
      status: AttendanceStatus,
      sessionId: Uuid,
      projectId?: Uuid
    ) => void
    simplifiedProfile?: boolean
  }
>(
  (
    {
      type,
      students,
      sessionId,
      session,
      classGroup,
      onStudentAttendanceChange,
      simplifiedProfile,
    },
    ref
  ) => {
    const { currentUserId } = useUserContext()
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoaded, setIsLoaded] = useState(false)
    const isPortfolioClass = classGroup === CLASS_GROUPS.PORTFOLIO

    // The purple folder on the row navigates to the full-page portfolio
    // (JARVIS-126). `ensurePortfolioExists` still runs first so the doc and its
    // artwork backfill exist by the time the page subscribes.
    const handleOpenPortfolio = useCallback(
      async (studentId: Uuid) => {
        if (!currentUserId) return
        await ensurePortfolioExists(studentId, currentUserId as Uuid)
        navigate(getPortfolioPagePath(studentId), {
          state: { from: location.pathname },
        })
      },
      [currentUserId, navigate, location.pathname]
    )

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        onAnimationComplete={() => setIsLoaded(true)}
      >
        {students.length > 0 && (
          <Flex
            col
            style={{
              padding: 6,
            }}
          >
            <AttendanceSectionHeader type={type} count={students.length} />
            {isLoaded &&
              students?.map((item) => {
                if (!item.sessionId || typeof item.sessionId !== "string")
                  return null

                return (
                  <ComponentErrorBoundary
                    key={`attendance-row-${item.student.id}-${type}-${sessionId}`}
                    variant="line"
                    context={`Student: ${item.student.name}`}
                  >
                    <AttendanceRow
                      student={item.student}
                      status={item.status}
                      sessionId={item.sessionId}
                      classGroup={classGroup}
                      size={ATTENDANCE_ROW_SIZE}
                      simplifiedProfile={simplifiedProfile}
                      onStatusChange={(student, status) =>
                        onStudentAttendanceChange(
                          student,
                          status,
                          item.sessionId,
                          item.projectId
                        )
                      }
                      onOpenPortfolio={
                        isPortfolioClass ? handleOpenPortfolio : undefined
                      }
                      subtitle={
                        type === "makeups" ? (
                          <MissedMakeupSessionSubtitle
                            missedSessionId={item.sessionId as Uuid}
                            student={item.student}
                          />
                        ) : type === "exceptions" && session ? (
                          <StudentProjectDaySubtitle
                            student={item.student}
                            session={session}
                            status={item.status}
                          />
                        ) : undefined
                      }
                    />
                  </ComponentErrorBoundary>
                )
              })}
          </Flex>
        )}
      </motion.div>
    )
  }
)

TeacherAttendanceSection.displayName = "TeacherAttendanceSection"

export default TeacherAttendanceSection
